import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2, Square } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

/**
 * VoiceInput component - Provides voice recording and transcription
 * Uses OpenAI Whisper for speech-to-text
 * 
 * @param {function} onTranscription - Callback with transcribed text
 * @param {string} placeholder - Placeholder text when not recording
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable the component
 */
export const VoiceInput = ({ 
  onTranscription, 
  placeholder = "Cliquez pour parler...",
  className = "",
  disabled = false 
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.stop();
      }
      if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Audio level visualization
  const updateAudioLevel = () => {
    if (analyserRef.current && isRecording) {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);
      animationRef.current = requestAnimationFrame(updateAudioLevel);
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];
      
      // Setup audio analyzer for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        await transcribeAudio(audioBlob);
      };
      
      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Start audio level animation
      updateAudioLevel();
      
      toast.info('🎤 Enregistrement en cours...', { duration: 2000 });
      
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
      } else if (error.name === 'NotFoundError') {
        toast.error('Aucun microphone détecté sur cet appareil.');
      } else {
        toast.error('Erreur d\'accès au microphone: ' + error.message);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Stop animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      setAudioLevel(0);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 seconds timeout
      });
      
      const { text, response_time_ms } = response.data;
      
      if (text && text.trim()) {
        onTranscription(text.trim());
        toast.success(`✅ Transcription réussie (${(response_time_ms / 1000).toFixed(1)}s)`, {
          duration: 2000
        });
      } else {
        toast.warning('Aucun texte détecté. Réessayez en parlant plus clairement.');
      }
      
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Enregistrement trop court ou invalide');
      } else {
        toast.error('Erreur de transcription: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleClick = () => {
    if (disabled || isTranscribing) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isTranscribing}
        data-testid="voice-input-btn"
        aria-label={placeholder}
        className={`
          relative flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-xl 
          font-medium transition-all duration-300 min-w-[48px] min-h-[48px] sm:min-w-[140px]
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isRecording 
            ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg shadow-red-500/30 animate-pulse' 
            : isTranscribing
              ? 'bg-gradient-to-r from-amber-600 to-amber-500 text-white'
              : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105'
          }
        `}
        title={isRecording ? "Cliquez pour arrêter" : "Cliquez pour parler"}
      >
        {/* Audio level indicator ring */}
        {isRecording && (
          <div 
            className="absolute inset-0 rounded-xl border-2 border-white/50 animate-ping"
            style={{ 
              transform: `scale(${1 + audioLevel * 0.3})`,
              opacity: 0.5 + audioLevel * 0.5
            }}
          />
        )}
        
        {isTranscribing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="hidden sm:inline">Transcription...</span>
          </>
        ) : isRecording ? (
          <>
            <Square className="w-4 h-4 fill-current" />
            <span>{formatTime(recordingTime)}</span>
            <div className="flex gap-0.5 items-center ml-1">
              {[...Array(3)].map((_, i) => (
                <div 
                  key={i}
                  className="w-1 bg-white rounded-full transition-all duration-100"
                  style={{ 
                    height: `${8 + audioLevel * 16 * (i === 1 ? 1 : 0.6)}px`,
                    animationDelay: `${i * 100}ms`
                  }}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <Mic className="w-5 h-5" />
            <span className="hidden sm:inline">{placeholder}</span>
          </>
        )}
      </button>
      
      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50">
          <div className="absolute inset-0 bg-red-400 rounded-full animate-ping" />
        </div>
      )}
    </div>
  );
};

export default VoiceInput;
